'use client';

import { useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';

import { Alert, AlertLevel, AlertType } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { GlobalAlertContext } from '../../organisms/GlobalAlertProvider/GlobalAlertContext';
import { sanitizeHtml } from '../../shared/utils';

import { getAlertStyling } from './global-alert-portal.styling';
import { useGlobalAlert } from './hooks';

/**
 * GlobalAlertPortal is a generic component that renders alerts in a portal.
 * It reads alert configuration from GlobalAlertContext and displays it.
 */
export const GlobalAlertPortal = () => {
  const { openAlert, setOpenAlert, alertContent } =
    useContext(GlobalAlertContext);
  const { brand } = usePageContext();
  const { container } = useGlobalAlert(openAlert);

  const handleAlertClose = useCallback(() => {
    setOpenAlert(false);
  }, [setOpenAlert]);

  // Don't render if no container or no alert content
  if (!container || !alertContent) return null;

  // When isHtml is true, render the title as sanitized HTML (supports rich
  // text with links authored in AEM Universal Editor)
  const titleContent = alertContent.isHtml ? (
    <span
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(alertContent.title) }}
      data-testid="global-alert-html-title"
    />
  ) : (
    alertContent.title
  );

  return createPortal(
    <Alert
      sx={getAlertStyling(brand)}
      open={openAlert}
      onClose={handleAlertClose}
      title={titleContent}
      description={alertContent.description || ''}
      level={alertContent.level || AlertLevel.LEVEL_1}
      type={alertContent.type || AlertType.ERROR}
      closable={true}
    />,
    container,
  );
};
